import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFaq } from '@/hooks/useFaq'
import type { FaqItem } from '@/services/cms'

function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openIds, setOpenIds] = useState<Set<string>>(() =>
    items.length > 0 ? new Set([items[0].id]) : new Set()
  )

  const toggle = (id: string) => {
    setOpenIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="flex flex-col gap-4">
      {items.map(item => {
        const isOpen = openIds.has(item.id)
        return (
          <div
            key={item.id}
            className={`border-border rounded-xl border p-10 transition-shadow duration-300 ease-in-out ${isOpen ? 'bg-white shadow-[0px_20px_95px_0px_#C9CBCC4D]' : 'bg-[#FAFAFA]'}`}
          >
            <button
              type="button"
              onClick={() => toggle(item.id)}
              className="cursor-pointer text-left"
              aria-expanded={isOpen}
            >
              <div className="flex items-start gap-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center">
                  <img
                    src={isOpen ? '/minus.svg' : '/plus.svg'}
                    alt=""
                    className="max-h-6 max-w-6 object-contain"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-foreground text-base font-medium">{item.question}</p>
                </div>
              </div>
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              }`}
            >
              <div className="overflow-hidden">
                <div
                  className="text-faq-answer faq-answer-content [&_a]:text-primary pt-4 text-base font-normal [&_a]:underline [&_a]:underline-offset-2 [&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h4]:mb-2 [&_h4]:text-base [&_h4]:font-semibold [&_li]:mb-1 [&_li:last-child]:mb-0 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5"
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function FaqPage() {
  const { t } = useTranslation()
  const { items, isLoading, isError, error } = useFaq()

  return (
    <div
      className="flex min-h-[calc(100vh-12rem)] flex-1 flex-col bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/faq-bg.svg')" }}
    >
      <div className="mx-auto w-full max-w-[676px] px-4 py-10">
        <h1 className="text-foreground mb-10 text-center text-[40px] font-bold">
          {t('faq.title')}
        </h1>

        {isLoading && (
          <p className="text-foreground text-center text-base font-normal">{t('faq.loading')}</p>
        )}

        {isError && (
          <p className="text-destructive text-center text-base font-normal">
            {error instanceof Error ? error.message : t('faq.error')}
          </p>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <p className="text-foreground text-center text-base font-normal">{t('faq.empty')}</p>
        )}

        {!isLoading && !isError && items.length > 0 && (
          <FaqAccordion key={items[0].id} items={items} />
        )}
      </div>
    </div>
  )
}
